import { corptaxCustomLightTheme } from '@cscglobal-tbs/react-components-core';
import { FluentProvider, makeStyles, Tooltip } from '@fluentui/react-components';
import { DocumentAdd16Filled, DocumentAdd16Regular, DocumentDataLink16Filled, DocumentDataLink16Regular } from '@fluentui/react-icons';
import {
    PdfComboBoxField,
    PdfDocument,
    PdfFormFieldVisibility,
    PdfListBoxField,
    PdfListField,
    PdfPage,
    PdfTextBoxField,
} from '@syncfusion/ej2-pdf';
import i18n from 'i18next';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { StatementFilled } from '../assets/icons';
import { ISelectedEntity } from '../contexts/formViewContext';
import { IWhitePaperReport, ReviewFormField, TaxReturnTOCItem, ViewFormResponseFormFields } from '../model';
import { customPalette } from '../theme';
import { IInteractiveFormField, InteractiveFieldBorder } from './InteractiveFieldHitboxes';
import {
    ELIMINATION_COLUMN_ID,
    getFilteredFormFieldsDrillDown,
    resolveReportCellForMatchedField,
    SUBTOTAL_COLUMN_ID,
    TOTAL_COLUMN_ID,
} from './ReportUtils';
import {
    appendAppearanceStamps,
    collectFieldAppearanceStamp,
    getPageRotationDegrees,
    IFieldAppearanceStamp,
    isStampableRotation,
    mapTopLeftBoundsForRotation90,
} from './RotatedPageFieldStamping';
import { findFirstTaxReturnTOCItem } from './TaxReturnTOCUtils';

export const isDescriptiveDataField = (
    matchedField: ReviewFormField | undefined,
    currentWhitePaperReport: IWhitePaperReport | undefined,
    selectedEntity: ISelectedEntity | undefined,
    isEnhancedDescriptiveDataEnabled: boolean
) => {
    if (!currentWhitePaperReport) {
        return false;
    }

    if (isEnhancedDescriptiveDataEnabled) {
        if (!currentWhitePaperReport.columns) {
            return false;
        }

        const resolved = resolveReportCellForMatchedField(matchedField, currentWhitePaperReport, selectedEntity?.index);
        const currentColumn = resolved?.currentColumn;
        const descriptiveDataColumnIndex: number | undefined =
            [TOTAL_COLUMN_ID, ELIMINATION_COLUMN_ID, SUBTOTAL_COLUMN_ID].indexOf(currentColumn?.originalColumnId ?? 0) === -1
                ? currentColumn?.originalColumnId
                : currentColumn?.columnIndex;

        return (currentWhitePaperReport.descriptiveData?.[descriptiveDataColumnIndex ?? -1]?.[matchedField?.row ?? -1]?.length ?? 0) > 0;
    } else {
        if (!currentWhitePaperReport.legacyDescriptiveDataFields || !matchedField?.row) {
            return false;
        }

        return Object.prototype.hasOwnProperty.call(currentWhitePaperReport.legacyDescriptiveDataFields, matchedField.row);
    }
};

export const getFieldsDictionary = (formFields: ViewFormResponseFormFields | undefined): Record<string, ReviewFormField> => {
    if (!formFields) return {};
    return Object.values(formFields)
        .flat()
        .reduce((acc, field) => {
            if (field.pdfFieldName) {
                acc[field.pdfFieldName] = field;
            }
            return acc;
        }, {} as Record<string, ReviewFormField>);
};

const useStatementIconStyles = makeStyles({
    iconContainer: {
        color: customPalette.grey222222,
        display: 'inline-flex',
        ':hover, :active': {
            color: customPalette.blue075A92,
            backgroundColor: customPalette.statusCoolMidGrey,
        },
    },
});

const createIconWithHover = (
    IconComponent: React.FC<React.SVGProps<SVGSVGElement>>,
    IconHoverComponent: React.FC<React.SVGProps<SVGSVGElement>>,
    tooltipContent: string | undefined
): React.FC<{ iconSize: number }> => {
    return ({ iconSize }) => {
        const styles = useStatementIconStyles();
        const [isHovered, setIsHovered] = React.useState(false);

        const currentIcon = isHovered ? IconHoverComponent : IconComponent;

        const iconElement = React.createElement(currentIcon, {
            style: { height: `${iconSize}px`, width: `${iconSize}px` },
        });

        const divProps = {
            className: styles.iconContainer,
            tabIndex: 0,
            onMouseEnter: () => setIsHovered(true),
            onMouseLeave: () => setIsHovered(false),
        };

        const trigger = React.createElement('div', divProps, iconElement);

        const content = tooltipContent
            ? React.createElement(
                  Tooltip,
                  {
                      content: tooltipContent,
                      relationship: 'description',
                      appearance: 'inverted',
                      withArrow: true,
                      positioning: 'after',
                  },
                  trigger
              )
            : trigger;

        return content;
    };
};

export interface IFlattenPdfFormFieldsOptions {
    formFields: ViewFormResponseFormFields | undefined;
    currentWhitePaperReport: IWhitePaperReport | undefined;
    isFormsViewDrilldownEnabled: boolean;
    isEditFormViewDescriptiveDataEnabled: boolean;
    isDetailedWorkspacesEnabled: boolean;
    dataNameToTocItemMap: Map<string, TaxReturnTOCItem>;
    emptyDetailedWorkspaceItemsMap: Map<string, TaxReturnTOCItem>;
    checkboxBackgroundColor: string;
    selectedEntity: ISelectedEntity | undefined;
    isEnhancedDescriptiveDataEnabled: boolean;
    /** Which report column to fall back to when a field's dataName carries no column index — see resolveReportCellForMatchedField. */
    selectedEntityIndex?: number;
    /**
     * Gates form-field comments (indicator triangle, comment menu item) — mirrors
     * DefaultPDFViewer's own isFormFieldCommentsEnabled (isCollaborationUIEnabled &&
     * CalcReviewFormCommentsEnabled && hasEditableFields), not isCollaborationUIEnabled alone,
     * so the indicator can't render for a field the "View Comments" menu item won't offer.
     */
    isFormFieldCommentsEnabled?: boolean;
}

// Exported so DefaultPDFViewer can re-derive an already-flattened field's comment state after
// report comment metadata refreshes, without a full PDF re-flatten — see refreshCommentIndicators.
export const getOverridableFieldCommentState = (
    matchedField: ReviewFormField | undefined,
    isFormFieldCommentsEnabled: boolean | undefined,
    currentWhitePaperReport: IWhitePaperReport | undefined,
    selectedEntityIndex: number | undefined
): { overridable: boolean; hasUnresolvedComments: boolean; hasOnlyResolvedComments: boolean; cellDisplayValue?: string } => {
    if (!isFormFieldCommentsEnabled || !matchedField?.overridable) {
        return { overridable: false, hasUnresolvedComments: false, hasOnlyResolvedComments: false };
    }

    // A field can be flagged overridable by the backend without its row existing in the
    // current report (e.g. same condition that hides the "Report Drill down" item) - there's
    // no report cell to anchor a comment to, so treat it as non-overridable rather than
    // offering a "View Comments" item that silently does nothing when clicked. The row's
    // displayRowNumber and the column's fieldName are what the comment thread URL/API are keyed
    // on, so a row or column missing either one can't anchor a comment either.
    const resolved = resolveReportCellForMatchedField(matchedField, currentWhitePaperReport, selectedEntityIndex);
    if (!resolved?.row?.displayRowNumber || !resolved.currentColumn?.fieldName) {
        return { overridable: false, hasUnresolvedComments: false, hasOnlyResolvedComments: false };
    }

    const cellValue =
        resolved.currentColumn.fieldName && resolved.row.values ? resolved.row.values[resolved.currentColumn.fieldName] : undefined;
    const hasUnresolvedComments = (cellValue?.totalUnresolvedComments ?? 0) > 0;
    const hasOnlyResolvedComments = !hasUnresolvedComments && (cellValue?.totalComments ?? 0) > 0;

    return { overridable: true, hasUnresolvedComments, hasOnlyResolvedComments, cellDisplayValue: cellValue?.current ?? undefined };
};

export interface IFlattenPdfFormFieldsResult {
    base64Pdf: string;
    /** One entry per text field — display-only fields included, so every field gets a focusable hitbox. */
    interactiveFields: IInteractiveFormField[];
}

// Every text field is flattened so the page keeps the field's original appearance stream.
// The Syncfusion viewer re-renders live AcroForm fields from field properties using browser
// font metrics, which differ from the fonts the appearance streams were generated with and
// clip text in tightly-sized field boxes (PBI 5940244). Click/focus/right-click behavior is
// provided instead by overlay hitboxes (see InteractiveFieldHitboxes.ts) built from the
// geometry collected here for every text field.
export const flattenPdfFormFields = (pdfData: string, options: IFlattenPdfFormFieldsOptions): IFlattenPdfFormFieldsResult => {
    const {
        formFields,
        currentWhitePaperReport,
        isFormsViewDrilldownEnabled,
        isEditFormViewDescriptiveDataEnabled,
        isDetailedWorkspacesEnabled,
        dataNameToTocItemMap,
        emptyDetailedWorkspaceItemsMap,
        checkboxBackgroundColor,
        selectedEntity,
        isEnhancedDescriptiveDataEnabled,
        selectedEntityIndex,
        isFormFieldCommentsEnabled,
    } = options;
    const document = new PdfDocument(pdfData);
    // Must run before any field is accessed so parsing happens against the sorted order.
    sortFieldsByAnnotationZOrder(document);
    const form = document.form;

    const allowedDrillDownContextList = getFilteredFormFieldsDrillDown(formFields || null, currentWhitePaperReport);
    const fieldsDict = getFieldsDictionary(formFields);
    const interactiveFields: IInteractiveFormField[] = [];
    const acroFormFieldNames = new Set<string>();

    const pageIndexByPage = new Map<unknown, number>();
    const rotationByPage = new Map<unknown, number>();
    for (let pageIndex = 0; pageIndex < document.pageCount; pageIndex++) {
        const page = document.getPage(pageIndex);
        pageIndexByPage.set(page, pageIndex);
        rotationByPage.set(page, getPageRotationDegrees(page));
    }

    // ej2-pdf's flatten stamps appearances outside the media box on /Rotate 90 pages (see
    // RotatedPageFieldStamping) — those pages get spec-correct stamps collected here and
    // appended before save, while ej2's flatten still removes the widget annotations.
    const rotatedPageStamps = new Map<PdfPage, IFieldAppearanceStamp[]>();
    const collectRotatedStamp = (page: PdfPage, field: unknown): boolean => {
        const stamp = collectFieldAppearanceStamp(document, field);
        if (!stamp) {
            return false;
        }
        const stamps = rotatedPageStamps.get(page);
        if (stamps) {
            stamps.push(stamp);
        } else {
            rotatedPageStamps.set(page, [stamp]);
        }
        return true;
    };
    const isOnStampableRotatedPage = (field: { page?: PdfPage }): PdfPage | null => {
        try {
            const page = field.page;
            return page && isStampableRotation(rotationByPage.get(page) ?? 0) ? page : null;
        } catch {
            return null;
        }
    };

    for (let i = 0; i < form.count; i++) {
        const field = form.fieldAt(i);
        if (field.name) {
            acroFormFieldNames.add(field.name);
        }
        if (field instanceof PdfComboBoxField || field instanceof PdfListBoxField) {
            prepareChoiceField(field, checkboxBackgroundColor);
            continue;
        }
        if (!(field instanceof PdfTextBoxField)) {
            field.readOnly = true;
            field.backColor = hexToRgbArray(checkboxBackgroundColor);
            continue;
        }

        if (field.name) {
            const matchedField = fieldsDict[field.name];
            // Focus and tooltip hitboxes are collected for every text field regardless of the
            // drilldown flag; only the right-click drilldown/edit-data behavior is flag-gated.

            const isDescriptiveField = isDescriptiveDataField(
                matchedField,
                currentWhitePaperReport,
                selectedEntity,
                isEnhancedDescriptiveDataEnabled
            );
            const isDrilldown = isFormsViewDrilldownEnabled && !!allowedDrillDownContextList[field.name];
            const isDescriptiveData = isFormsViewDrilldownEnabled && isDescriptiveField;
            // The pre-flatten viewer's blue-border rule (drilldown signifier per PBI 4693069
            // plus flag-gated descriptive-data fields), reproduced verbatim: the border is
            // gated independently of the right-click behavior above.
            const hasInteractiveBorder = isDrilldown || (isEditFormViewDescriptiveDataEnabled && isDescriptiveField);
            // The backend reports which border rule the PDF generation applied per field
            // (pdfBorderStyle); the interactive blue only ever upgrades the default black.
            // 'authored' widgets keep their own border, so the hitbox draws none. Fields
            // without metadata get the default treatment, matching the generation's default.
            const pdfBorderStyle = matchedField?.pdfBorderStyle;
            const border: InteractiveFieldBorder =
                pdfBorderStyle === 'none' || pdfBorderStyle === 'authored'
                    ? 'none'
                    : pdfBorderStyle === 'numberedStatement'
                    ? 'numberedStatement'
                    : hasInteractiveBorder
                    ? 'interactive'
                    : 'default';
            const dataName = matchedField?.dataName ?? undefined;
            const tooltipText = matchedField?.tooltip || field.toolTip || undefined;
            // Match the SMART tooltip format: field data name on the first line, text below.
            const tooltip = tooltipText ? (dataName ? `${dataName}\n${tooltipText}` : tooltipText) : undefined;
            const isStatement =
                isDetailedWorkspacesEnabled &&
                !!dataName &&
                (dataNameToTocItemMap.has(dataName) || emptyDetailedWorkspaceItemsMap.has(dataName));

            try {
                const page = field.page;
                const pageIndex = pageIndexByPage.get(page);
                const size = page?.size;
                if (pageIndex !== undefined && size) {
                    // On a /Rotate 90 page the viewer displays the page rotated: hitbox
                    // bounds must be in display space and scale against the displayed width.
                    const isRotated = isStampableRotation(rotationByPage.get(page) ?? 0);
                    const { overridable, hasUnresolvedComments, hasOnlyResolvedComments, cellDisplayValue } =
                        getOverridableFieldCommentState(
                            matchedField,
                            isFormFieldCommentsEnabled,
                            currentWhitePaperReport,
                            selectedEntityIndex
                        );
                    interactiveFields.push({
                        name: field.name,
                        pageIndex,
                        bounds: isRotated ? mapTopLeftBoundsForRotation90(field.bounds, size) : field.bounds,
                        pageWidth: isRotated ? size[1] : size[0],
                        dataName,
                        row: matchedField?.row,
                        tooltip,
                        isDrilldown,
                        isDescriptiveData,
                        border,
                        isStatement,
                        overridable,
                        hasUnresolvedComments,
                        hasOnlyResolvedComments,
                        cellDisplayValue,
                        isNumeric: !!matchedField?.isNumeric,
                    });
                }
            } catch {
                // Geometry unavailable for this field — it is still flattened below and
                // renders correctly; it just cannot offer a hitbox.
            }
        }

        const rotatedPage = isOnStampableRotatedPage(field);
        if (rotatedPage) {
            collectRotatedStamp(rotatedPage, field);
        }
        field.flatten = true;
    }

    // Indicator/marker fields (e.g. e-file requirement markers) have no widget in the PDF —
    // the backend paints their red indicator into the page graphics and supplies the widget
    // geometry it recorded at build time via pdfPageIndex/pdfBounds. Give them tooltip-only
    // hitboxes so hovering the indicator shows the same tooltip SMART does.
    if (formFields) {
        Object.values(formFields)
            .flat()
            .forEach((metadataField) => {
                const bounds = metadataField.pdfBounds;
                const pageIndex = metadataField.pdfPageIndex;
                const tooltipText = metadataField.tooltip || metadataField.errorMessage || '';
                if (
                    !metadataField.pdfFieldName ||
                    acroFormFieldNames.has(metadataField.pdfFieldName) ||
                    !bounds ||
                    pageIndex === null ||
                    pageIndex === undefined ||
                    pageIndex < 0 ||
                    pageIndex >= document.pageCount ||
                    !tooltipText
                ) {
                    return;
                }

                const page = document.getPage(pageIndex);
                const size = page?.size;
                if (!size) {
                    return;
                }

                acroFormFieldNames.add(metadataField.pdfFieldName);
                // pdfBounds uses PDF user space (origin bottom-left); hitboxes use top-left.
                const topLeftBounds = {
                    x: bounds.left ?? 0,
                    y: size[1] - ((bounds.bottom ?? 0) + (bounds.height ?? 0)),
                    width: bounds.width ?? 0,
                    height: bounds.height ?? 0,
                };
                const isRotated = isStampableRotation(rotationByPage.get(page) ?? 0);
                interactiveFields.push({
                    name: metadataField.pdfFieldName,
                    pageIndex,
                    bounds: isRotated ? mapTopLeftBoundsForRotation90(topLeftBounds, size) : topLeftBounds,
                    pageWidth: isRotated ? size[1] : size[0],
                    dataName: metadataField.dataName ?? undefined,
                    row: metadataField.row,
                    tooltip: metadataField.dataName ? `${metadataField.dataName}\n${tooltipText}` : tooltipText,
                    isDrilldown: false,
                    isDescriptiveData: false,
                    border: 'none',
                    isStatement: false,
                    // Marker/indicator fields have no widget and are never overridable.
                    overridable: false,
                    hasUnresolvedComments: false,
                    hasOnlyResolvedComments: false,
                });
            });
    }

    rotatedPageStamps.forEach((stamps, page) => {
        // Fields were visited in DESCENDING annotation order (sortFieldsByAnnotationZOrder),
        // so reverse to paint stamps in ascending annotation order — the z-order Acrobat
        // uses, keeping overlapping fields correct on rotated pages too.
        appendAppearanceStamps(document, page, stamps.slice().reverse());
    });

    const updated = document.save();
    const base64Pdf = byteArrayToBase64Pdf(updated);
    return { base64Pdf, interactiveFields };
};

interface IPdfFieldInternals {
    _dictionary?: { get?: (key: string) => unknown };
}

interface IPdfObjectReference {
    objectNumber?: number;
}

interface IPdfPageInternals {
    _pageDictionary?: { get?: (key: string) => unknown };
}

interface IPdfFormInternals {
    _fields?: IPdfObjectReference[];
}

// ej2-pdf flattens fields by iterating its internal field array in REVERSE (PdfForm._doPostProcess),
// so flattened appearance streams stamp onto the page in reverse array order. Where field
// rectangles overlap (e.g. the 851's corporation-name row over the address row), that paints the
// wrong field on top and clips the one underneath — the opposite of Acrobat, which draws widgets
// in page annotation order. Sorting the field array in DESCENDING annotation order makes the
// reverse iteration stamp in true annotation z-order. Relies on ej2-pdf internals; fails soft
// (original order, current behavior) if the internals change.
const sortFieldsByAnnotationZOrder = (document: PdfDocument): void => {
    try {
        const annotationRank = new Map<number, number>();
        let rank = 0;
        for (let pageIndex = 0; pageIndex < document.pageCount; pageIndex++) {
            const page = document.getPage(pageIndex) as unknown as IPdfPageInternals;
            const annotations = page._pageDictionary?.get?.('Annots');
            if (!Array.isArray(annotations)) {
                continue;
            }
            for (const reference of annotations as IPdfObjectReference[]) {
                const objectNumber = reference?.objectNumber;
                if (typeof objectNumber === 'number' && !annotationRank.has(objectNumber)) {
                    annotationRank.set(objectNumber, rank++);
                }
            }
        }

        const fields = (document.form as unknown as IPdfFormInternals)._fields;
        if (annotationRank.size === 0 || !Array.isArray(fields)) {
            return;
        }

        const indexed = fields.map((reference, index) => ({
            reference,
            index,
            rank: annotationRank.get(reference?.objectNumber ?? -1) ?? Number.MAX_SAFE_INTEGER,
        }));
        indexed.sort((a, b) => b.rank - a.rank || b.index - a.index);
        for (let i = 0; i < indexed.length; i++) {
            fields[i] = indexed[i].reference;
        }
    } catch {
        // Keep the original order — rendering falls back to the previous stamping behavior.
    }
};

const PDF_ANNOTATION_FLAG_HIDDEN = 2;
const PDF_ANNOTATION_FLAG_NO_VIEW = 32;

// Combo/list fields cannot be flattened reliably (ej2-pdf regenerates their appearance
// instead of stamping it). Most Corptax combos carry the NoView flag and have a visible
// companion text field that shows the value, so they only need to be hidden (the viewer
// ignores NoView but honors Hidden). Genuinely visible combos stay LIVE and read-only:
// the Syncfusion viewer then renders the native dropdown chrome (arrow) exactly as the
// pre-flatten viewer and SMART do, while the read-only flag plus the DOM-level guard in
// DefaultPDFViewer keep them non-interactive — matching production, where dropdowns are
// not clickable. Live widgets are positioned by the viewer, so rotated pages need no
// special handling here.
const prepareChoiceField = (field: PdfListField, backgroundColor: string): void => {
    const internals = field as unknown as IPdfFieldInternals;
    const readKey = (key: string): unknown => {
        try {
            return internals._dictionary?.get?.(key);
        } catch {
            return undefined;
        }
    };

    // field.visibility resolves the /F flags from the widget annotation as well as the field
    // dictionary (they live on a separate /Kids widget when the dictionaries are unmerged).
    let isViewable: boolean;
    try {
        const visibility = field.visibility;
        isViewable = visibility === PdfFormFieldVisibility.visible || visibility === PdfFormFieldVisibility.visibleNotPrintable;
    } catch {
        const flags = typeof readKey('F') === 'number' ? (readKey('F') as number) : 0;
        isViewable = (flags & (PDF_ANNOTATION_FLAG_HIDDEN | PDF_ANNOTATION_FLAG_NO_VIEW)) === 0;
    }

    if (isViewable) {
        try {
            field.readOnly = true;
            field.backColor = hexToRgbArray(backgroundColor);
        } catch {
            // Marking read-only failed — the field stays live and editable-looking for
            // this one widget (pre-fix behavior); selections never persist anywhere.
        }
        return;
    }

    try {
        field.visibility = PdfFormFieldVisibility.hidden;
    } catch {
        // Hiding failed — leave the field live rather than aborting the whole flatten;
        // the viewer then renders this one helper field as a dropdown (pre-fix behavior).
    }
};

const hexToRgbArray = (hex: string): [number, number, number] => {
    hex = hex.replace(/^#/, '');

    const bigint = parseInt(hex, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return [r, g, b];
};

const byteArrayToBase64Pdf = (byteArray: Uint8Array): string => {
    let binary = '';
    const len = byteArray.byteLength;
    for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(byteArray[i]);
    }
    const base64 = window.btoa(binary);
    return 'data:application/pdf;base64,' + base64;
};

// Store icon roots for cleanup
const iconRoots = new Map<string, ReturnType<typeof createRoot>>();

// Get or create the content container
const getContentContainer = (): HTMLElement | null => {
    const pdfViewerContainer = document.getElementById('formViewerPDF');
    return (
        (pdfViewerContainer?.querySelector('.e-pv-viewer-container') as HTMLElement) ||
        (pdfViewerContainer?.querySelector('.e-pv-main-container') as HTMLElement) ||
        (pdfViewerContainer as HTMLElement)
    );
};

const calculateIconSize = (rect: DOMRect): number => {
    return rect.height;
};

const hideFormFieldAndGetSize = (formFieldElement: HTMLElement): DOMRect => {
    const rect = formFieldElement.getBoundingClientRect();
    const iconSize = calculateIconSize(rect);

    formFieldElement.style.backgroundColor = 'transparent';
    formFieldElement.style.color = 'transparent';
    formFieldElement.style.border = 'none';
    formFieldElement.style.width = `${iconSize}px`;

    return rect;
};

const addOpenModalEventHandlerToElement = (
    element: HTMLElement,
    eventHandler?: (fieldDataName: string, formFieldElement?: HTMLElement) => void,
    fieldDataName?: string
): void => {
    element.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();

            if (eventHandler && fieldDataName) {
                eventHandler(fieldDataName, element);
            }
        }
    });
};

// Update icon position and size based on field position and zoom
const updateExistingIcon = (
    formFieldElement: HTMLElement,
    fieldId: string,
    onIconClick?: (fieldDataName: string, formFieldElement?: HTMLElement) => void
): void => {
    const existingIcon = document.querySelector(`[data-statement-icon="${fieldId}"]`) as HTMLElement;
    if (!existingIcon) {
        return;
    }

    const fieldDataName = existingIcon.getAttribute('data-field-data-name') ?? undefined;
    const contentContainer = getContentContainer();
    if (!contentContainer) {
        return;
    }

    const rect = hideFormFieldAndGetSize(formFieldElement);
    addOpenModalEventHandlerToElement(formFieldElement, onIconClick, fieldDataName);

    if (rect.width <= 0 || rect.height <= 0) {
        return;
    }

    const containerRect = contentContainer.getBoundingClientRect();
    const iconSize = calculateIconSize(rect);

    existingIcon.style.left = `${rect.left - containerRect.left + contentContainer.scrollLeft}px`;
    existingIcon.style.top = `${rect.top - containerRect.top + contentContainer.scrollTop}px`;
    existingIcon.style.width = `${iconSize}px`;
    existingIcon.style.height = `${iconSize}px`;

    // Update icon container size to scale proportionally
    const iconElement = existingIcon.querySelector('svg') as SVGElement;
    if (iconElement) {
        iconElement.style.width = `${iconSize}px`;
        iconElement.style.height = `${iconSize}px`;
    }
};

export const createIconOverlayElement = (
    fieldId: string,
    rect: DOMRect,
    containerRect: DOMRect,
    contentContainer: HTMLElement,
    hasDetailsWorkspace: boolean,
    fieldDataName?: string
): HTMLDivElement => {
    const iconOverlay = document.createElement('div');
    iconOverlay.setAttribute('data-statement-icon', fieldId);
    iconOverlay.setAttribute('data-field-data-name', fieldDataName ?? '');
    iconOverlay.style.position = 'absolute';
    iconOverlay.style.left = `${rect.left - containerRect.left + contentContainer.scrollLeft}px`;
    iconOverlay.style.top = `${rect.top - containerRect.top + contentContainer.scrollTop}px`;
    iconOverlay.style.display = 'flex';
    iconOverlay.style.alignItems = 'center';
    iconOverlay.style.justifyContent = 'flex-end';
    iconOverlay.style.paddingTop = '2px';
    iconOverlay.style.paddingRight = '2px';
    iconOverlay.style.pointerEvents = hasDetailsWorkspace ? 'auto' : 'none';
    iconOverlay.style.cursor = hasDetailsWorkspace ? 'pointer' : 'default';
    iconOverlay.style.zIndex = '100';
    return iconOverlay;
};

export const createIconContainerElement = (iconOverlay: HTMLDivElement): HTMLDivElement => {
    const iconContainer = document.createElement('div');
    iconContainer.style.display = 'flex';
    iconContainer.style.alignItems = 'center';
    iconContainer.style.justifyContent = 'center';
    iconOverlay.appendChild(iconContainer);
    return iconContainer;
};

export const createStatementIconOverlay = (
    formFieldElement: HTMLElement,
    fieldId: string,
    fieldInfo?: { matchedField?: ReviewFormField; fieldDataName?: string },
    tocReferenceMap?: Map<string, TaxReturnTOCItem>,
    emptyDetailedWorkspaceItemsMap?: Map<string, TaxReturnTOCItem>,
    onIconClick?: (fieldDataName: string, formFieldElement?: HTMLElement) => void
): void => {
    // Check if icon already exists globally (not just in parent)
    const existingIcon = document.querySelector(`[data-statement-icon="${fieldId}"]`) as HTMLElement;
    if (existingIcon) {
        // Update icon if it already exists
        updateExistingIcon(formFieldElement, fieldId, onIconClick);
        return;
    }

    // Get position and size BEFORE hiding
    const rect = hideFormFieldAndGetSize(formFieldElement);

    if (rect.width <= 0 || rect.height <= 0) {
        return;
    }

    const contentContainer = getContentContainer();

    if (!contentContainer) {
        return;
    }

    const containerRect = contentContainer.getBoundingClientRect();

    // Check if this field has a details workspace
    const isEmptyDetailedWorkspace = fieldInfo?.fieldDataName
        ? emptyDetailedWorkspaceItemsMap?.has(fieldInfo?.fieldDataName) ?? false
        : false;
    const hasDetailsWorkspace = fieldInfo?.fieldDataName
        ? tocReferenceMap?.has(fieldInfo.fieldDataName) || isEmptyDetailedWorkspace
        : false;

    const iconOverlay = createIconOverlayElement(
        fieldId,
        rect,
        containerRect,
        contentContainer,
        hasDetailsWorkspace,
        fieldInfo?.fieldDataName
    );

    // Add click handler for fields with details workspaces
    if (hasDetailsWorkspace && fieldInfo && fieldInfo.fieldDataName) {
        iconOverlay.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();

            if (onIconClick && fieldInfo.fieldDataName) {
                onIconClick(fieldInfo.fieldDataName, formFieldElement);
            }
        });
        addOpenModalEventHandlerToElement(formFieldElement, onIconClick, fieldInfo.fieldDataName);

        formFieldElement.setAttribute('role', 'application');
        formFieldElement.ariaLabel = isEmptyDetailedWorkspace ? i18n.t('addDetails') : i18n.t('editDetails');
    }

    const iconSize = calculateIconSize(rect);

    const iconContainer = createIconContainerElement(iconOverlay);

    // Ensure container is positioned
    const containerStyle = getComputedStyle(contentContainer);
    if (containerStyle.position === 'static') {
        contentContainer.style.position = 'relative';
    }

    contentContainer.appendChild(iconOverlay);

    // Render React icon component
    const root = createRoot(iconContainer);
    iconRoots.set(fieldId, root);

    const renderIcon = (detailedWorkspaceIsEmpty: boolean, iconSize: number) => {
        let IconComponent: React.FC<React.SVGProps<SVGSVGElement>>;
        if (hasDetailsWorkspace) {
            IconComponent = detailedWorkspaceIsEmpty
                ? (DocumentAdd16Regular as React.FC<React.SVGProps<SVGSVGElement>>)
                : (DocumentDataLink16Regular as React.FC<React.SVGProps<SVGSVGElement>>);
        } else {
            IconComponent = StatementFilled as React.FC<React.SVGProps<SVGSVGElement>>;
        }

        let IconHoverComponent: React.FC<React.SVGProps<SVGSVGElement>>;
        if (hasDetailsWorkspace) {
            IconHoverComponent = detailedWorkspaceIsEmpty
                ? (DocumentAdd16Filled as React.FC<React.SVGProps<SVGSVGElement>>)
                : (DocumentDataLink16Filled as React.FC<React.SVGProps<SVGSVGElement>>);
        } else {
            IconHoverComponent = StatementFilled as React.FC<React.SVGProps<SVGSVGElement>>;
        }

        const tooltipContent = hasDetailsWorkspace ? (detailedWorkspaceIsEmpty ? i18n.t('addDetails') : i18n.t('editDetails')) : undefined;

        const IconWithHover = createIconWithHover(IconComponent, IconHoverComponent, tooltipContent);

        root.render(
            React.createElement(FluentProvider, { theme: corptaxCustomLightTheme }, React.createElement(IconWithHover, { iconSize }))
        );
    };

    renderIcon(isEmptyDetailedWorkspace, iconSize);
};

// Update all icon positions (called on zoom/scroll)
export const updateAllStatementIconPositions = (): void => {
    const pdfViewerContainer = document.getElementById('formViewerPDF');
    if (!pdfViewerContainer) {
        return;
    }

    // Get all statement icons
    const icons = pdfViewerContainer.querySelectorAll('[data-statement-icon]');

    icons.forEach((iconElement) => {
        const fieldId = iconElement.getAttribute('data-statement-icon');
        if (!fieldId) {
            return;
        }

        // Find the corresponding form field
        const formFieldElement = document.getElementById(fieldId);
        if (formFieldElement) {
            updateExistingIcon(formFieldElement, fieldId);
        }
    });
};

// Remove icon for a specific field
export const removeStatementIcon = (fieldId: string): void => {
    const existingIcon = document.querySelector(`[data-statement-icon="${fieldId}"]`) as HTMLElement;
    if (existingIcon) {
        const root = iconRoots.get(fieldId);
        if (root) {
            root.unmount();
            iconRoots.delete(fieldId);
        }
        existingIcon.remove();
    }
};

// Remove all statement icons
export const removeAllStatementIcons = (): void => {
    const pdfViewerContainer = document.getElementById('formViewerPDF');
    if (!pdfViewerContainer) {
        return;
    }

    const icons = pdfViewerContainer.querySelectorAll('[data-statement-icon]');

    icons.forEach((iconElement) => {
        const fieldId = iconElement.getAttribute('data-statement-icon');
        if (fieldId) {
            removeStatementIcon(fieldId);
        }
    });
};

export const tocContainsEntityCodes = (tocData: TaxReturnTOCItem[] | null | undefined): boolean => {
    if (!tocData) {
        return false;
    }
    const firstItem = findFirstTaxReturnTOCItem(tocData, (item: TaxReturnTOCItem) => Boolean(item.entityCode && item.isForm));
    return !!firstItem;
};

export const focusElementAfterFormModal = (elementToFocusAfterFormModal: string): void => {
    const elementByid = document.getElementById(elementToFocusAfterFormModal);
    if (!elementByid) {
        document.getElementsByName(elementToFocusAfterFormModal)?.[0]?.focus();
    } else {
        elementByid.focus();
    }
};
